// useUsers.ts
import { useEffect, useState } from "react";
import axios from "axios";
import Config from "../../../config";

export interface User {
  uuid: string;
  roles: string[];
  username: string;
  email: string;
  school?: string;
  profession?: string;
  discipline?: string;
  how_heard_us?: string;
  is_oauth: boolean;
  auth_type: string;
  finished_signup: boolean;
  last_login?: Date;
  stars?: number;
}

export interface GetUsersResponse {
  users: User[];
  page: number;
  limit: number;
  total: number;
}

export interface GetAllRolesResponse {
  roles: string[];
}

const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [roles, setRoles] = useState<string[]>([]);
  const [total, setTotal] = useState<number>(0);

  const getUserStars = async (uuid: string): Promise<number> => {
    try {
      const response = await axios.get(`${Config.apiDomain}/api/admin/users/${uuid}/stars`);
      return response.data.star_balance;
    } catch (error: any) {
      console.error("Error fetching user stars:", error);
      return 0;
    }
  };

  const updateUserRoles = async (uuid: string, updatedRoles: string[]) => {
    try {
      await axios.put(
        `${Config.apiDomain}/api/admin/users/update_roles`,
        {
          uuid,
          roles: updatedRoles, // Send the updated roles list
        }
      );
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.uuid === uuid ? { ...user, roles: updatedRoles } : user
        )
      );
    } catch (error: any) {
      alert(error.response?.data?.detail || "Error updating roles");
      console.error("Error updating user roles:", error);
      throw error;
    }
  };

  const fetchUsers = async (page: number, limit: number) => {
    setLoading(true);
    try {
      const response = await axios.get<GetUsersResponse>(
        `${Config.apiDomain}/api/admin/users`,
        {
          params: { page, limit },
        }
      );
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error: any) {
      alert(error.response?.data?.detail || "Error fetching users");
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get<GetAllRolesResponse>(
        `${Config.apiDomain}/api/admin/roles`
      );
      setRoles(response.data.roles);
    } catch (error: any) {
      console.error("Error fetching roles:", error);
    }
  };

  const searchUsersByEmail = async (emailPattern: string) => {
    setLoading(true);
    try {
      const response = await axios.get<GetUsersResponse>(
        `${Config.apiDomain}/api/admin/users/search`,
        {
          params: { email: emailPattern },
        }
      );
      setUsers(response.data.users);
      setTotal(response.data.total); // Update the total if the API supports it.
    } catch (error: any) {
      alert(error.response?.data?.detail || "Error searching users");
      console.error("Error searching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateUserStars = async (uuid: string, stars: number) => {
    try {
      await axios.put(`${Config.apiDomain}/api/admin/users/update_stars`, {
        uuid: uuid,
        star_balance:stars
      });
    } catch (error: any) {
      alert(error.response?.data?.detail || "Error updating user stars");
      console.error("Error updating user stars:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  return { users, roles, loading, total, fetchUsers, updateUserRoles, searchUsersByEmail, updateUserStars, getUserStars };
};

export default useUsers;
